import { useFilterStore } from '@Portal/stores/filter';
import {
  getDataForProcessFlowObject,
  getProcessFlowObjectHistory,
  getProcessFlowTemplateObjects,
  getQueues,
  getValidMentionableUsers,
  getValidUserAllocations,
  getProcessFlowQueueCounts,
} from '@Shared/api/portal';
import { generalisedMetricLabel } from '@Shared/components/visuals/utility';
import { useRootStore } from '@Shared/stores/root';
import { UserDisplayInfo } from '@Shared/types/portal/PortalFeatures';
import {
  ProcessFlowCard,
  ProcessFlowQueue,
  TemplateField,
  ProcessFlowQueueCount,
} from '@Shared/types/portal/ProcessFlow';
import { defineStore, storeToRefs } from 'pinia';

// This is used to lift up the processflow 'current' card away from an awkward shared value across most of the frontline dashboard
// to something more common across the system.
// Ideally, some refactoring should put more code here over time, but keeping it simple to see how it goes for now.
export const useProcessFlowStore = defineStore('processFlow', {
  state: () => {
    return {
      isLoading: true,
      // we default the card to an empty card so that we can still do some rough layout
      // this way the visual animation of it sliding across looks better
      currentCard: {
        id: '',
        program: '',
        processFlow: {
          processFlowObjectId: undefined,
          state: '',
          subState: '',
          actionGroups: [],
          isTerminatingState: false,
          history: [],
        },
        primaryMetric: {
          value: '',
          friendlyName: '',
          index: 0,
          label: '',
          localeTitle: '',
        },
        summaryVerbatim: undefined,
        metrics: [],
        drivers: [],
        responded: [],
        title: [],
        location: [],
        headerTop: [],
        headerBottom: [],
        footer: [],
        timeline: [],
        contact: {
          customerName: undefined,
          title: [],
          email: undefined,
          mobile: undefined,
          other: [],
        },
        verbatimQuestions: [],
        driverQuestions: [],
        metricQuestions: [],
        details: [],
        categorisedDetails: {} as Record<string, TemplateField[]>,
        facets: {} as Record<string, string | null>,
      },
      processFlowModuleId: null,
      smartDataModuleId: null,
      validMentionableUsers: null,
      validUserAllocations: null,
      queues: [],
      cardsToDisplay: [],
      responseCount: 0,
      skip: 0,
      queueCounts: [],
      selectedQueue: null,
    } as {
      isLoading: boolean;
      currentCard: ProcessFlowCard;
      smartDataModuleId: string | null;
      processFlowModuleId: string | null;
      validMentionableUsers: UserDisplayInfo[] | null;
      validUserAllocations: UserDisplayInfo[] | null;
      queues: ProcessFlowQueue[];
      cardsToDisplay: ProcessFlowCard[];
      responseCount: number;
      skip: number;
      queueCounts: ProcessFlowQueueCount[];
      selectedQueue: ProcessFlowQueue | null;
    };
  },
  getters: {
    primaryMetricLabel(): string {
      if (!this.currentCard) return 'Unknown';
      return generalisedMetricLabel(this.currentCard.primaryMetric.value);
    },
  },
  actions: {
    async setup() {
      if (useRootStore().portalUri) {
        // no need to call this for every card, we can just do it once upon page load
        this.validMentionableUsers = await getValidMentionableUsers(this.processFlowModuleId ?? undefined);
        this.validUserAllocations = await getValidUserAllocations();
      }
    },
    resetCurrentCard() {
      this.currentCard = {
        id: '',
        program: '',
        processFlow: {
          processFlowObjectId: undefined,
          state: '',
          subState: '',
          actionGroups: [],
          isTerminatingState: false,
          history: [],
        },
        primaryMetric: {
          value: '',
          friendlyName: '',
          index: 0,
          label: '',
          localeTitle: '',
        },
        summaryVerbatim: undefined,
        metrics: [],
        drivers: [],
        responded: [],
        title: [],
        location: [],
        headerTop: [],
        headerBottom: [],
        footer: [],
        timeline: [],
        contact: {
          customerName: undefined,
          title: [],
          email: undefined,
          mobile: undefined,
          other: [],
        },
        verbatimQuestions: [],
        driverQuestions: [],
        metricQuestions: [],
        details: [],
        categorisedDetails: {} as Record<string, TemplateField[]>,
        facets: {} as Record<string, string | null>,
      };
    },
    async loadHistory() {
      if (
        !this.processFlowModuleId ||
        !this.currentCard ||
        this.currentCard.processFlow.processFlowObjectId == undefined
      )
        return;
      this.currentCard.processFlow.history = (
        await getProcessFlowObjectHistory(this.processFlowModuleId, this.currentCard.processFlow.processFlowObjectId!)
      ).history;
    },
    async loadSingleDetailCard(moduleId: string, sourceObjectId: string) {
      const result = await getDataForProcessFlowObject(moduleId, sourceObjectId);
      // if we already have history because the history api call returned quicker then the card info we store that history
      const history = this.currentCard.processFlow.history;
      this.currentCard = result.processFlowTemplateObject;
      // and restore the history if it had some actions
      if (history && history.length > 0) {
        this.currentCard.processFlow.history = history;
      }
      this.processFlowModuleId = moduleId;
      this.smartDataModuleId = result.view.smartDataModuleId;
      this.isLoading = false;
    },
    async loadProcessFlowQueues(moduleId: string) {
      this.queues = await getQueues(moduleId);
      this.smartDataModuleId = this.queues[0].smartDataModuleId;
      this.processFlowModuleId = moduleId;
    },
    async loadDataForProcessFlowQueue(moduleId: string, queue: ProcessFlowQueue) {
      const filterStore = useFilterStore();
      const { selectedPrimaryFilters } = storeToRefs(filterStore);

      const response = await getProcessFlowTemplateObjects(moduleId, queue.name, {
        smartFilters: selectedPrimaryFilters.value,
        orderByFields: [queue.defaultOrderBy.fieldKey],
        orderByOperator: queue.defaultOrderBy.operatorAsString,
        skip: this.skip,
        top: queue.view.recordsPerPage,
      });
      this.cardsToDisplay = response.data;
      this.responseCount = response.recordCount;
      this.isLoading = false;
    },
    async loadQueueCounts(moduleId: string) {
      if (!moduleId) return;

      this.queueCounts = await getProcessFlowQueueCounts(moduleId);
    },
  },
  persist: {
    paths: [],
  },
});
